import { useMemo, useState } from "react";
import type { PersonLeaderboardRow } from "../../shared/types";
import {
  assignRanks,
  nextSortRule,
  sortPersonLeaderboard,
  type SortKey,
  type SortRule,
} from "../../shared/sorting";
import SortableHeader from "./SortableHeader";

export default function PersonLeaderboard({
  rows,
  compact,
}: {
  rows: PersonLeaderboardRow[];
  compact?: boolean;
}) {
  const [sort, setSort] = useState<SortRule | null>(null);

  const ranked = useMemo(() => assignRanks(rows), [rows]);

  const sorted = useMemo(() => {
    if (!sort) return ranked;
    return sortPersonLeaderboard(ranked, sort).map((row) => {
      const matched = ranked.find((r) => r.person === row.person)!;
      return matched;
    });
  }, [ranked, sort]);

  const current: SortRule =
    sort ?? { key: "teamsRemaining", direction: "desc" };

  const handleSort = (key: SortKey) => setSort((s) => nextSortRule(s, key));

  return (
    <table className="leaderboard-table" aria-label="Person leaderboard">
      <thead>
        <tr>
          <th aria-label="Rank">#</th>
          <SortableHeader
            label="Person"
            sortKey="person"
            current={current}
            onSort={handleSort}
          />
          <SortableHeader
            label={compact ? "Teams" : "Teams left"}
            sortKey="teamsRemaining"
            current={current}
            onSort={handleSort}
            numeric
          />
          <SortableHeader
            label="GD"
            sortKey="goalDifference"
            current={current}
            onSort={handleSort}
            numeric
          />
          <SortableHeader
            label="GF"
            sortKey="goalsFor"
            current={current}
            onSort={handleSort}
            numeric
          />
          <SortableHeader
            label="Pts"
            sortKey="points"
            current={current}
            onSort={handleSort}
            numeric
          />
          <SortableHeader
            label="GA"
            sortKey="goalsAgainst"
            current={current}
            onSort={handleSort}
            numeric
            className="desktop-only"
          />
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <tr key={row.person}>
            <td className="rank-cell">{row.rank}</td>
            <td>{row.person}</td>
            <td className="numeric">{row.teamsRemaining}</td>
            <td className="numeric">{formatGD(row.goalDifference)}</td>
            <td className="numeric">{row.goalsFor}</td>
            <td className="numeric">{row.points}</td>
            <td className="numeric desktop-only">{row.goalsAgainst}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatGD(n: number) {
  if (n > 0) return `+${n}`;
  return String(n);
}
